import React, { useContext, useEffect } from 'react'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { useForm } from 'react-hook-form'
import { t } from '../../i18n/i18n'
import { ModalActions } from '../common/modal/Modal'
import { ControlledMoneyInput } from '../common/inputs/ControlledMoneyInput'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { Grid } from '../common/grid/Grid'
import { GridItem } from '../common/grid/GridItem'
import { Button } from '@fluentui/react-components'
import { Workplace, Process, ProtectedAssetGoalType, ProtectedAssetGoal } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { addProtectedAssetGoal, deleteProtectedAssetGoal, isSystemAdminOrCreator, updateProtectedAssetGoal } from '../../helpers/settingsHelper'
import { ControlledSingleDropdown } from '../common/inputs/ControlledSingleDropdown'
import { DraggableModal } from '../common/modal/DraggableModal'
import { MoreOptionsMenuButton } from '../common/other/MoreOptionsMenuButton'
import { isSystemAdmin } from '../../helpers/permissionHelper'
import { Form } from '../common/form/Form'

type ModalUpsertProtectedAssetGoalProps = {
   selectedItem: ProtectedAssetGoal
   open: boolean
   setOpen: React.Dispatch<React.SetStateAction<boolean>>
   allProcesses: Process[]
   allWorkplaces: Workplace[]
   isProtectedAsset: boolean
   isProtectedAssetGoalInUseMap?: Record<string, boolean>
}
export const ModalUpsertProtectedAssetGoal = ({
   selectedItem,
   open,
   setOpen,
   allProcesses,
   allWorkplaces,
   isProtectedAsset,
   isProtectedAssetGoalInUseMap,
}: ModalUpsertProtectedAssetGoalProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount, organisation } = dataContext.state
   const {
      control,
      reset,
      handleSubmit,
      formState: { disabled, isSubmitting, isDirty },
   } = useForm<any>({ disabled: selectedItem ? selectedItem.id && !isSystemAdminOrCreator(selectedItem, userAccount) : false })
   const itemName = isProtectedAsset ? t('ProtectedAsset') : t('OrganisationalGoal')
   const assetOrGoal = isProtectedAsset ? t('Asset') : t('TheGoal')

   const isWorkplaceDisabled = organisation.workplaceLimit === 0
   const defaultWorkplace = allWorkplaces.find((wp) => wp.isDefault)

   const close = () => {
      reset()
      setOpen(false)
   }

   useEffect(() => {
      if (selectedItem) {
         reset({
            ...selectedItem,
            process: allProcesses.find((p) => p.text === selectedItem.process)?.id || '',
            workplace: selectedItem.workplace?.id || (allWorkplaces.length === 1 ? allWorkplaces[0].id : ''),
         })
      } else {
         reset({
            name: null,
            description: null,
            valuation: null,
            workplace: defaultWorkplace ? defaultWorkplace.id : '',
            process: '',
            valuationDate: null,
            comment: null,
         })
      }
   }, [allWorkplaces, allProcesses, open, reset, selectedItem, userAccount, isWorkplaceDisabled, defaultWorkplace])

   const onSubmit = async (data: any) => {
      data.workplace = allWorkplaces.find((b) => b.id === data.workplace)
      data.process = allProcesses.find((p) => p.id === data.process).text
      if (isProtectedAsset) {
         data.type = ProtectedAssetGoalType.ProtectedAsset
      } else {
         data.type = ProtectedAssetGoalType.OrganisationalGoal
      }

      if (data.id) {
         await updateProtectedAssetGoal(close, dataContext, data)
         reset(data)
      } else {
         await addProtectedAssetGoal(close, dataContext, data)
      }
   }

   const deleteOptions = () => {
      const isInUse = isProtectedAssetGoalInUseMap[selectedItem.id] ?? false
      const userHasPermission = isSystemAdmin(userAccount)

      const deleteDisabled = !userHasPermission || isInUse

      let deleteTooltipMessage = ''

      if (deleteDisabled) {
         if (!userHasPermission) {
            deleteTooltipMessage = isProtectedAsset
               ? t('OnlySystemAdministratorCanDeleteProtectedAssets')
               : t('OnlySystemAdministratorCanDeleteOrganisationalGoals')
         } else {
            deleteTooltipMessage = isProtectedAsset ? t('TheProtectedAssetIsInUseAndCannotBeDeleted') : t('TheOrganisationalGoalIsInUseAndCannotBeDeleted')
         }
      }
      const itemLabel = isProtectedAsset ? t('ProtectedAsset') : t('OrganisationalGoal')
      const deleteMessage = t('DeleteItem', { Item: itemLabel.toLowerCase() })

      const deleteConfirmationMessage = t('DeleteItemConfirmationMessage', {
         Item: isProtectedAsset ? t('TheProtectedAsset').toLowerCase() : t('TheOrganisationalGoal').toLowerCase(),
      })

      return (
         <MoreOptionsMenuButton
            item={selectedItem}
            deleteFunction={deleteProtectedAssetGoal}
            setState={dataContext.setRootState}
            disabled={deleteDisabled}
            disabledToolTipMessage={deleteTooltipMessage}
            deleteMessage={deleteMessage}
            deleteConfirmationMessage={deleteConfirmationMessage}
            closeOnDelete={close}
         />
      )
   }

   return (
      <DraggableModal
         open={open}
         onClose={close}
         title={
            selectedItem
               ? t('EditItem', { Item: itemName.toLowerCase() })
               : isProtectedAsset === false
               ? t('NewOrganisationalGoal')
               : t('NewItem', { Item: itemName.toLowerCase() })
         }
         secondaryButton={selectedItem ? deleteOptions : null}
      >
         <Form onSubmit={handleSubmit(onSubmit)}>
            <Grid>
               <GridItem size="12/12">
                  <ControlledTextInput label={t('NameOfItem', { Item: assetOrGoal.toLowerCase() })} control={control} name="name" required />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput
                     label={t('DescriptionOfItem', { Item: assetOrGoal.toLowerCase() })}
                     control={control}
                     name="description"
                     multiline
                     required
                  />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledMoneyInput label={t('ValuationOfItem', { Item: assetOrGoal.toLowerCase() })} control={control} name="valuation" />
               </GridItem>
               <GridItem size="6/12">
                  <ControlledDatePicker label={t('ValuationDate')} control={control} name="valuationDate" />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledSingleDropdown
                     label={t('WorkplaceAffiliation')}
                     control={control}
                     name="workplace"
                     options={allWorkplaces.map((b) => ({ key: b.id, text: b.name }))}
                     disabled={isWorkplaceDisabled || disabled}
                     required
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledSingleDropdown
                     label={t('ProcessAffiliation')}
                     control={control}
                     name="process"
                     options={allProcesses.map((p) => ({ key: p.id, text: p.text }))}
                     required
                     disabled={disabled}
                  />
               </GridItem>
               <GridItem size="12/12">
                  <ControlledTextInput label={t('Comment')} control={control} name="comment" multiline />
               </GridItem>
               <GridItem size="12/12">
                  <ModalActions>
                     <Button onClick={close} disabled={isSubmitting}>
                        {disabled ? t('Close') : t('Cancel')}
                     </Button>

                     {!disabled && (
                        <Button appearance="primary" type="submit" disabled={!isDirty || isSubmitting}>
                           {t(selectedItem ? 'Update' : 'Save')}
                        </Button>
                     )}
                  </ModalActions>
               </GridItem>
            </Grid>
         </Form>
      </DraggableModal>
   )
}
